import React, { ReactElement, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { formatPort, separatePorts } from "helpers/docker";

import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { replicaRegex } from "constants/regexp";

import IconSelector from "components/shared/images/IconSelector";

import { getClusterReplicas } from "redux/handlers/clusterHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { IContainerData } from "types/api/dockerApiInterface";

import NoReplicasFound from "./NoReplicasFound";

const ClusterServerReplicas = ({
 id,
 reloadData,
 onReload
}: {
 id: number;
 reloadData: boolean;
 onReload: () => void;
}): ReactElement => {
 const dispatch = useAppDispatch();

 const searchRef = useRef<HTMLInputElement>(null);

 const [container, setContainers] = useState<Array<IContainerData>>([]);
 const [containerNumber, setContainersNumber] = useState<number>(0);
 const [isLoading, setIsLoading] = useState<boolean>(true);
 const [listWasEmpty, setListWasEmpty] = useState<boolean>(false);
 const [currentPage, setCurrentPage] = useState<number>(0);
 const [rowPerPage, setRowPerPage] = useState<number>(10);

 useEffect(() => {
  (async () => {
   if (reloadData) {
    setIsLoading(true);
    const containerList = await dispatch(
     getClusterReplicas(id, currentPage, rowPerPage, searchRef?.current?.value)
    );
    if (containerList.dataset.length === 0) setListWasEmpty(true);
    const filteredContainers = containerList.dataset.filter((element) =>
     replicaRegex.test(element.name)
    );
    setContainers(filteredContainers);
    setContainersNumber(filteredContainers.length);
    setIsLoading(false);
   }
   onReload();
  })();
 }, [reloadData]);

 const handleOnRowsPerPageChange = async (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
 ) => {
  const newRowsPerPage = parseInt(event.target.value, 10);
  setRowPerPage(newRowsPerPage);
  setCurrentPage(0);
  const containerList = await dispatch(
   getClusterReplicas(id, 0, newRowsPerPage, searchRef?.current?.value)
  );
  const filteredContainers = containerList.dataset.filter((element) =>
   replicaRegex.test(element.name)
  );
  setContainers(filteredContainers);
  setContainersNumber(filteredContainers.length);
 };

 const handleOnPageChange = async (
  event: React.MouseEvent<HTMLButtonElement> | null,
  newPage: number
 ) => {
  const startIndex = newPage * rowPerPage;
  setCurrentPage(newPage);
  const containerList = await dispatch(
   getClusterReplicas(id, startIndex, rowPerPage, searchRef?.current?.value)
  );
  const filteredContainers = containerList.dataset.filter((element) =>
   replicaRegex.test(element.name)
  );
  setContainers(filteredContainers);
  setContainersNumber(filteredContainers.length);
 };

 return (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack direction="row" spacing={1}>
    <IconSelector icon="ServiceIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
    <Typography fontSize={16} variant="unboundedTitle">
     <FormattedMessage id="cluster.replicas" />
    </Typography>
   </Stack>
   <Stack pt={2}>
    {isLoading ? (
     <Stack direction="row" alignItems="center" justifyContent="center">
      <CircularProgress />
     </Stack>
    ) : container.length > 0 || !listWasEmpty ? (
     <Stack>
      <TableContainer component="div">
       <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
         <TableRow>
          <TableCell>
           <FormattedMessage id="docker.container.name" />
          </TableCell>
          <TableCell>
           <FormattedMessage id="docker.container.status" />
          </TableCell>
          <TableCell>
           <FormattedMessage id="docker.container.image" />
          </TableCell>
          <TableCell>
           <FormattedMessage id="docker.container.ports" />
          </TableCell>
         </TableRow>
        </TableHead>
        <TableBody>
         {container.map((row, index) => (
          <TableRow key={`isp-config-list-${index}`}>
           <TableCell component="th" scope="row">
            <Stack spacing={1}>
             <Typography variant="caption" fontWeight="bold">
              {row.name.substring(0, row.name.indexOf(".") + 2)}
             </Typography>
             <Typography variant="caption">{`Source: ${
              row.replicated_on || "not available"
             }`}</Typography>
            </Stack>
           </TableCell>
           <TableCell component="th" scope="row">
            <Chip size="small" label={row.status} color="success" />
           </TableCell>
           <TableCell component="th" scope="row">
            <Typography variant="caption">{row.image}</Typography>
           </TableCell>
           <TableCell component="th" scope="row">
            <Stack direction="row" spacing={1}>
             {separatePorts(row.ports).map((element, key) => {
              return (
               <Chip
                key={`docker-port-${key}`}
                label={formatPort(element)}
                size="small"
                color="info"
               />
              );
             })}
            </Stack>
           </TableCell>
          </TableRow>
         ))}
        </TableBody>
       </Table>
      </TableContainer>
      <TablePagination
       component="div"
       count={containerNumber}
       page={currentPage}
       rowsPerPage={rowPerPage}
       onPageChange={handleOnPageChange}
       onRowsPerPageChange={handleOnRowsPerPageChange}
      />
     </Stack>
    ) : (
     <NoReplicasFound />
    )}
   </Stack>
  </Paper>
 );
};

export default ClusterServerReplicas;
